/**
 * Card "Saldo atual em conta" — fonte Sync (M2M), saldo PARCIAL 3+5NA.
 *
 * Troca da âncora: o valor vem do /api/saldos do Sync (conciliação Open
 * Finance × Omie), não mais do saldo_inicial do Omie. O Sync monitora 3 contas
 * (Itaú, Caixa, Bradesco); os outros 5 bancos PJ Indra (BS2, BB, Safra, Banco
 * XP, Basa) ainda NÃO têm Open Finance → aparecem como **NA** (sem valor). Por
 * isso o saldo é parcial por construção e o card rotula "saldo parcial · N
 * monitoradas · M NA".
 *
 * Fechado: valor grande = saldo_total_atual (soma SÓ das 3 monitoradas) +
 * sub "saldo parcial · 3 monitoradas · 5 NA".
 * Aberto: as monitoradas com valor + frescor "atualizado às HH:MM"; as NA
 * listadas com chip "NA" e sem valor.
 *
 * Sync indisponível (fonte_disponivel=false): mostra o último conhecido com
 * tom "fonte indisponível" (is_real=false) — NUNCA reverte pro saldo Omie de 8
 * bancos. Sem dado conhecido → "—".
 */
import { useId, useMemo, useState } from "react";
import { parseUtcNaive } from "../../utils/dateBr";
import { ChevronDown, DollarSign } from "lucide-react";
import { fmtBRL } from "../../utils/formatters";
import { useSaldoV2 } from "../../hooks/useSaldoV2";
import type { ContaSaldoV2 } from "../../api/types";

interface Props {
  /** Placeholder de carregamento (mock saldoAtualTotal) enquanto o /api/saldos
   * não respondeu. NUNCA usado como valor real — fonte indisponível mostra
   * último conhecido ou "—", nunca o saldo Omie antigo. */
  total: number;
  sub?: string;
  /** Força o tom "fonte indisponível" mesmo com dado fresco (demos). */
  awaitingIntegration?: boolean;
}

interface ContaLinha {
  banco: string;
  valor: number | null;
  monitorada: boolean;
  is_real: boolean;
  atualizado_em: string | null;
  agencia: string | null;
  conta: string | null;
}

/**
 * Ordena contas pra renderização: monitoradas primeiro (por saldo desc),
 * depois as NA (ordem da API). As NA não somem — viram a linha colapsada
 * "Sem dados: ..." no painel (UX 03/07 item 5).
 */
export function _ordenarContas(contas: ContaSaldoV2[]): ContaLinha[] {
  const linhas = contas.map((c) => ({
    banco: c.banco_nome,
    valor: c.saldo_atual,
    monitorada: c.monitorada,
    is_real: c.is_real,
    atualizado_em: c.atualizado_em,
    agencia: c.agencia,
    conta: c.conta,
  }));
  linhas.sort((a, b) => {
    if (a.monitorada !== b.monitorada) return a.monitorada ? -1 : 1;
    return (b.valor ?? 0) - (a.valor ?? 0);
  });
  return linhas;
}

function _maskAgencia(ag: string | null, conta: string | null): string | null {
  if (!ag && !conta) return null;
  const last = (s: string) => (s.length > 4 ? s.slice(-4) : s);
  const a = ag ? `Ag ${ag}` : null;
  const c = conta ? `**** ${last(conta)}` : null;
  return [a, c].filter(Boolean).join(" · ");
}

/** "atualizado às HH:MM" a partir do ISO atualizado_em (hora local).
 * F-25a-1: sem offset na string → UTC naive do backend (parseUtcNaive);
 * com offset explícito, respeita. */
export function _formatAtualizado(iso: string | null): string {
  if (!iso) return "sem data";
  const d = parseUtcNaive(iso);
  if (Number.isNaN(d.getTime())) return "sem data";
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `atualizado às ${hh}:${mm}`;
}

export function SaldoAtualCard({ total, sub, awaitingIntegration }: Props) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const { data, isLoading } = useSaldoV2();

  const monitoradas = data?.monitoradas ?? 0;
  const na = data?.na ?? 5;
  const fonteIndisponivel = !!data && (!data.fonte_disponivel || awaitingIntegration);

  const contas: ContaLinha[] = useMemo(
    () => (data?.contas ? _ordenarContas(data.contas) : []),
    [data],
  );
  const contasMonitoradas = contas.filter((c) => c.monitorada);
  // UX 03/07 item 5: bancos NA colapsam numa linha só ("Sem dados: ...") — a
  // lista aberta 5x "NA" enterrava as 3 linhas com valor.
  const nomesNa = contas.filter((c) => !c.monitorada).map((c) => c.banco);

  // Há algum valor monitorado conhecido? Define se mostramos número ou "—".
  const temValor = contasMonitoradas.some((c) => c.valor !== null);

  // KPI grande ABREVIADO (item 7); detalhe/total CHEIOS (item 5 — nunca truncar).
  let valorTexto: string;
  let valorCheio: string | null = null;
  if (!data && isLoading) {
    valorTexto = fmtBRL(total, { abbreviate: true }); // placeholder de carregamento
    // valorCheio fica null: "Total monitorado" NAO mostra o mock cheio como
    // se fosse auditado enquanto o /api/saldos nao respondeu (review 03/07).
  } else if (data && temValor) {
    valorTexto = fmtBRL(data.saldo_total_atual, { abbreviate: true });
    valorCheio = fmtBRL(data.saldo_total_atual, { decimals: 0 });
  } else {
    valorTexto = "—";
  }

  // Rótulo parcial honesto: "saldo parcial · 3 monitoradas · 5 NA".
  const rotuloParcial = `saldo parcial · ${monitoradas} ${monitoradas === 1 ? "monitorada" : "monitoradas"} · ${na} NA`;
  // #69 P1: figura econômica separada (nunca somada no KPI acima).
  const eco = data?.economico ?? null;
  const maxValor = contas.reduce((m, b) => ((b.valor ?? 0) > m ? (b.valor ?? 0) : m), 0);

  return (
    // a11y (item 8b): o toggle é um <button> de verdade SÓ no header; o painel
    // é IRMÃO (não descendente de role=button com <ul> dentro, que quebrava
    // leitores de tela e aninhava interativos).
    <div
      className="rounded-xl border border-white/10 bg-indra-azul-surface p-5 flex flex-col gap-2 h-full"
      data-testid="saldo-atual-card"
    >
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((v) => !v)}
        className="flex flex-col gap-2 text-left cursor-pointer rounded-lg focus:outline-none focus:ring-2 focus:ring-indra-turquesa/40"
        data-testid="saldo-atual-toggle"
      >
        <div className="flex items-center justify-between gap-2 w-full">
          <span className="text-[11px] tracking-wider text-white/60">
            Saldo atual em conta
          </span>
          <span className="text-white/40">
            <DollarSign size={16} />
          </span>
        </div>

        <div className="flex items-center justify-between gap-2 w-full">
          <div
            className="text-2xl font-black text-white font-sans whitespace-nowrap tabular-nums"
            data-testid="saldo-atual-total"
          >
            {valorTexto}
          </div>
          <ChevronDown
            size={18}
            className={
              "text-white/60 flex-shrink-0 transition-transform duration-200 " +
              (open ? "rotate-180" : "")
            }
          />
        </div>

        {/* Rótulo parcial sempre visível: deixa explícito que é cobertura parcial. */}
        <div className="text-xs text-indra-turquesa/80" data-testid="saldo-parcial-label">
          {rotuloParcial}
        </div>
        {/* #69 P1 (UX 20/08): a figura econômica existe SEM abrir o card —
            escondida atrás do clique, o Veiga não a viu. Rotulada "fora
            deste saldo": nunca lê como parte do KPI acima. */}
        {eco?.investimentos_disponivel &&
          eco.investimentos_total_conservador !== null && (
            <div
              className="text-xs text-white/60"
              data-testid="saldo-aplicacoes-hint"
            >
              + aplicações{" "}
              {fmtBRL(eco.investimentos_total_conservador, { abbreviate: true })}{" "}
              — fora deste saldo · detalhe ao abrir
            </div>
          )}
        {fonteIndisponivel && (
          <div className="text-[11px] text-amber-400/80" data-testid="saldo-fonte-indisponivel">
            fonte indisponível — último saldo conhecido
          </div>
        )}
        {sub && <div className="text-xs text-white/60">{sub}</div>}
      </button>

      <div
        id={panelId}
        className={
          "grid transition-all duration-200 ease-out " +
          (open ? "grid-rows-[1fr] opacity-100 mt-1" : "grid-rows-[0fr] opacity-0")
        }
      >
        <div className="overflow-hidden">
          <ul className="space-y-2 pt-3 border-t border-white/10">
            {contasMonitoradas.map((b) => {
              // saldo negativo (cheque especial) NAO vira width negativa/100% (review 03/07)
              const pct = maxValor > 0 && b.valor && b.valor > 0 ? (b.valor / maxValor) * 100 : 0;
              const meta = _maskAgencia(b.agencia, b.conta);
              return (
                <li
                  key={`${b.banco}-${b.agencia ?? ""}-${b.conta ?? ""}`}
                  className="flex flex-col gap-1"
                  data-testid="saldo-conta-linha"
                >
                  {/* Linha principal: NOME (esq, pode truncar) + VALOR (dir,
                      nunca truncar) — antes o valor cortava (UX item 5). */}
                  <div className="flex items-center justify-between gap-2 min-w-0">
                    <span className="text-sm text-slate-200 truncate">{b.banco}</span>
                    <span className="text-sm font-mono tabular-nums font-medium text-slate-100 whitespace-nowrap flex-shrink-0">
                      {b.valor !== null ? fmtBRL(b.valor, { decimals: 0 }) : "—"}
                    </span>
                  </div>
                  {/* Sub-linha: agência/conta mascarada + frescor; a CONTA é a
                      parte que trunca se não couber, valor nunca. */}
                  <div className="flex items-center justify-between gap-2 min-w-0">
                    {meta ? (
                      <span className="text-[10px] text-white/60 font-mono truncate">
                        {meta}
                      </span>
                    ) : (
                      <span />
                    )}
                    <span
                      className={
                        "text-[10px] whitespace-nowrap flex-shrink-0 " +
                        (b.is_real ? "text-white/60" : "text-amber-400/70")
                      }
                    >
                      {_formatAtualizado(b.atualizado_em)}
                    </span>
                  </div>
                  <div className="h-1 bg-white/10 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-indra-turquesa/60 rounded-full transition-all"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </li>
              );
            })}
            {nomesNa.length > 0 && (
              <li
                className="text-[11px] text-white/60 pt-1"
                data-testid="saldo-contas-na"
              >
                Sem dados: {nomesNa.join(", ")}
              </li>
            )}
          </ul>

          <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between gap-2">
            <span className="text-sm font-semibold text-slate-200">
              Total monitorado
            </span>
            {/* Total CHEIO e inquebravel (item 5/7): abreviado fica so no KPI. */}
            <span className="text-sm font-mono tabular-nums font-semibold text-slate-100 whitespace-nowrap flex-shrink-0">
              {valorCheio ?? "—"}
            </span>
          </div>

          {/* #69 P1: figura ECONOMICA — SEPARADA e ROTULADA, nunca somada na
              ancora (o KPI grande segue DISPONIVEL). suprimido/stale VAO PRA
              TELA (existem pra nao esconder); buraco declarado idem. */}
          {eco && (
            <div
              className="mt-3 pt-3 border-t border-white/10 space-y-1"
              data-testid="saldo-economico"
            >
              <div className="text-[11px] tracking-wider text-white/60">
                Aplicações (fora do saldo acima)
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm text-slate-200">
                  Investimentos (deduplicado)
                </span>
                <span
                  className="text-sm font-mono tabular-nums text-slate-100 whitespace-nowrap flex-shrink-0"
                  data-testid="saldo-investimentos-total"
                >
                  {eco.investimentos_disponivel &&
                  eco.investimentos_total_conservador !== null
                    ? fmtBRL(eco.investimentos_total_conservador, { decimals: 0 })
                    : "indisponível"}
                </span>
              </div>
              {eco.investimentos_disponivel && (
                <>
                  <div
                    className="flex items-center justify-between gap-2 text-[11px] text-white/60"
                    data-testid="saldo-suprimido-duplicata"
                  >
                    <span>duplicata suprimida da soma</span>
                    <span className="font-mono tabular-nums">
                      {fmtBRL(eco.valor_suprimido_duplicata ?? 0, { decimals: 0 })}
                    </span>
                  </div>
                  <div
                    className="flex items-center justify-between gap-2 text-[11px] text-white/60"
                    data-testid="saldo-stale-nao-somado"
                  >
                    <span>posição velha não somada</span>
                    <span className="font-mono tabular-nums">
                      {fmtBRL(eco.valor_stale_nao_somado ?? 0, { decimals: 0 })}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-2 pt-1">
                    <span className="text-sm font-semibold text-slate-200">
                      Caixa econômico (corrente + aplicado)
                    </span>
                    <span
                      className="text-sm font-mono tabular-nums font-semibold text-slate-100 whitespace-nowrap flex-shrink-0"
                      data-testid="saldo-economico-total"
                    >
                      {eco.economico_total !== null
                        ? fmtBRL(eco.economico_total, { decimals: 0 })
                        : "—"}
                    </span>
                  </div>
                </>
              )}
              {/* 31/08: compromissada Itau — TERCEIRO numero, NUNCA somado nos
                  outros dois. valor so com estado ok; senao o motivo (nunca o
                  ultimo valor fingindo ser de hoje). data_base SEMPRE visivel;
                  stale => a tela DIZ que o numero e de anteontem. */}
              {eco.compromissada && eco.compromissada.estado !== null && (
                <div
                  className="flex flex-col gap-0.5 pt-1"
                  data-testid="saldo-compromissada"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm text-slate-200">
                      Compromissada Itaú (fora das somas)
                    </span>
                    <span
                      className="text-sm font-mono tabular-nums text-slate-100 whitespace-nowrap flex-shrink-0"
                      data-testid="saldo-compromissada-valor"
                    >
                      {eco.compromissada.estado === "ok" &&
                      eco.compromissada.valor_liquido_conforme_pdf !== null
                        ? fmtBRL(eco.compromissada.valor_liquido_conforme_pdf, {
                            decimals: 0,
                          })
                        : eco.compromissada.estado === "sem_posicao"
                          ? "sem posição aplicada"
                          : `indisponível (${eco.compromissada.motivo ?? eco.compromissada.estado})`}
                    </span>
                  </div>
                  {/* 01/09: sem_posicao e RESPOSTA de tesouraria, nao erro —
                      e a tela diz DE ONDE zerou (aviso literal do Sync:
                      tratar como erro/vazio inverte o sentido). */}
                  {eco.compromissada.estado === "sem_posicao" && (
                    <div
                      className="text-[10px] text-white/50"
                      data-testid="saldo-compromissada-sem-posicao"
                    >
                      posição zerada por resgate — posição anterior{" "}
                      {eco.compromissada.posicao_anterior != null
                        ? fmtBRL(eco.compromissada.posicao_anterior, {
                            decimals: 0,
                          })
                        : "—"}
                      {eco.compromissada.posicao_anterior_em
                        ? ` em ${eco.compromissada.posicao_anterior_em}`
                        : ""}
                    </div>
                  )}
                  {eco.compromissada.estado === "ok" && (
                    <div className="text-[10px] text-white/50">
                      data-base {eco.compromissada.data_base ?? "—"} ·{" "}
                      {eco.compromissada.premissa}
                    </div>
                  )}
                  {eco.compromissada.stale === true && (
                    <div
                      className="text-[10px] text-amber-400/80"
                      data-testid="saldo-compromissada-stale"
                    >
                      ⚠ data-base ANTIGA ({eco.compromissada.data_base}) — este
                      número não é de hoje
                    </div>
                  )}
                </div>
              )}
              <div
                className="text-[10px] text-white/50"
                data-testid="saldo-economico-cobertura"
              >
                {eco.contas_com_dado} de {eco.contas_servidas} contas com dado
              </div>
              {eco.buracos.map((b) => (
                <div
                  key={b}
                  className="text-[10px] text-amber-400/70"
                  data-testid="saldo-economico-buraco"
                >
                  ⚠ {b}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
